import { useEffect, useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Users,
  Share2,
  DollarSign,
  Award,
  ArrowLeft,
  Copy,
  Check,
  Network,
  User as UserIcon,
  Store,
  AlertCircle,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface NetworkNode {
  phone?: string;
  name?: string;
  depth?: number;
  totalOrderValue?: number;
  joinedAt?: string;
}

interface DashboardData {
  referralCode: string;
  totalCommission: number;
  paidCommission: number;
  unpaidCommission: number;
  directCount: number;
  network?: NetworkNode[];
}

export default function ReferralDashboard() {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  // 日期格式跟隨介面語言（zh-HK / en-GB）
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-GB' : 'zh-HK';
  const [data, setData] = useState<DashboardData | null>(null);
  const [network, setNetwork] = useState<NetworkNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertSuccess, setConvertSuccess] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('mydash:referrer.missingToken'));
      setLoading(false);
      return;
    }

    const loadDashboard = async () => {
      try {
        const res = await apiFetch(`/api/public/referral/dashboard/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || t('mydash:referrer.loadFailedStatus', { status: res.status }));
        }
        const dashboard: DashboardData = await res.json();
        setData(dashboard);

        const netRes = await apiFetch(`/api/public/referral/dashboard/${token}/network`);
        if (netRes.ok) {
          const netData = await netRes.json();
          setNetwork(Array.isArray(netData.network) ? netData.network : Array.isArray(netData) ? netData : []);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : t('mydash:referrer.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [token]);

  const shareUrl = data?.referralCode
    ? `${window.location.origin}/?gs=${encodeURIComponent(data.referralCode)}`
    : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleConvertToMerchant = async () => {
    if (!token) return;
    const confirmed = window.confirm(t('mydash:referrer.convertConfirm'));
    if (!confirmed) return;
    setConverting(true);
    setConvertError('');
    setConvertSuccess('');
    try {
      const res = await apiFetch('/api/public/referral/convert-to-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && !json.error) {
        setConvertSuccess(t('mydash:referrer.convertSuccess'));
      } else {
        setConvertError(json.error || t('mydash:referrer.convertFailed'));
      }
    } catch {
      setConvertError(t('mydash:referrer.networkError'));
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="animate-spin text-orange-600" size={40} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">{error || t('mydash:referrer.noData')}</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition"
          >
            <ArrowLeft size={18} /> {t('mydash:referrer.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-orange-600 dark:text-orange-400 hover:underline"
          >
            <ArrowLeft size={18} /> {t('mydash:referrer.backHome')}
          </Link>
          <LanguageSwitcher size="sm" />
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white shadow-lg">
            <Award size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{t('mydash:referrer.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('mydash:referrer.subtitle')}</p>
          </div>
        </div>

        {/* Referral code card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('mydash:referrer.myCode')}</p>
              <div className="text-3xl font-bold text-orange-600 tracking-wider">{data.referralCode}</div>
            </div>
            <div className="flex-1 sm:max-w-xs">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('mydash:referrer.shareLink')}</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-orange-600 text-white text-sm font-bold rounded-lg hover:bg-orange-700 transition"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? t('mydash:referrer.copied') : t('mydash:referrer.copy')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <DollarSign size={16} /> {t('mydash:referrer.totalCommission')}
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">HK${data.totalCommission}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <Check size={16} /> {t('mydash:referrer.paid')}
            </div>
            <div className="text-2xl font-bold text-green-600">HK${data.paidCommission}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <DollarSign size={16} /> {t('mydash:referrer.unpaid')}
            </div>
            <div className="text-2xl font-bold text-orange-600">HK${data.unpaidCommission}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <Users size={16} /> {t('mydash:referrer.direct')}
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{data.directCount}</div>
          </div>
        </div>

        {/* Network tree */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <Network size={20} className="text-orange-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('mydash:referrer.networkTitle')}</h2>
          </div>

          {network.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {t('mydash:referrer.networkEmpty')}
              <Share2 size={32} className="mx-auto mt-4 text-orange-200 dark:text-orange-900/40" />
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {network.map((node, idx) => (
                <li key={idx} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                      <UserIcon size={18} />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white text-sm">
                        {node.name || node.phone || t('mydash:referrer.anonymous')}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {node.depth !== undefined ? t('mydash:referrer.level', { level: node.depth + 1 }) : t('mydash:referrer.direct')}
                        {node.joinedAt ? ` · ${new Date(node.joinedAt).toLocaleDateString(dateLocale)}` : ''}
                      </div>
                    </div>
                  </div>
                  {node.totalOrderValue !== undefined && (
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        HK${node.totalOrderValue}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{t('mydash:referrer.totalSpent')}</div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ===== 成為鄰里商家 ===== */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
              <Store size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('mydash:referrer.convertTitle')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('mydash:referrer.convertIntro')}
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 font-medium">
                {t('mydash:referrer.convertWarning')}
              </p>
            </div>
          </div>

          {convertSuccess && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 text-sm flex items-start gap-2">
              <Check size={18} className="shrink-0 mt-0.5" />
              {convertSuccess}
            </div>
          )}
          {convertError && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              {convertError}
            </div>
          )}

          <button
            type="button"
            onClick={handleConvertToMerchant}
            disabled={converting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-stone-800 hover:bg-stone-900 dark:bg-stone-700 dark:hover:bg-stone-600 text-white font-bold rounded-xl transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {converting ? <Loader2 size={18} className="animate-spin" /> : <Store size={18} />}
            {converting ? t('mydash:referrer.converting') : t('mydash:referrer.convertBtn')}
          </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
